/**
 * OverflowRegion Atom — a horizontal scroller that is keyboard-reachable
 * ONLY while its content actually overflows.
 * @layer atom
 * @package @plerity/ui-core
 *
 * WCAG 2.1.1: a bare `overflow-x-auto` wrapper lets mouse/touch users reach
 * clipped columns at 375/768px, but a keyboard user cannot scroll a container
 * that holds no focusable element of its own. While overflowing this renders a
 * labelled, focusable `role="region"` (arrow keys then scroll it natively — no
 * key handler is attached, so the browser default is never swallowed). When the
 * content fits it stays a plain `div`, so a fitting table adds no Tab stop.
 *
 * `relative` keeps an `sr-only` (position:absolute) caption inside the clip, so
 * it cannot escape the scroller and pan the whole page sideways at 375px.
 *
 * Differs from `@plerity/markdown`'s `ScrollRegion` (always focusable, for
 * prose/code that is nearly always wider than its box).
 */

import { clsx } from "clsx";
import { type HTMLAttributes, type ReactNode, type RefObject, useEffect, useRef, useState } from "react";

/**
 * True while `ref`'s content is wider than its box (it horizontally scrolls).
 * Re-measured on mount, when the scroller OR its first child (the `<table>`,
 * which grows with data) resizes, and on window resize where ResizeObserver is
 * unavailable. Disconnects on unmount.
 */
export function useHorizontalOverflow(ref: RefObject<HTMLElement | null>, axis: OverflowAxis = "x"): boolean {
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () =>
      setOverflowing(el.scrollWidth > el.clientWidth + 1 || (axis === "both" && el.scrollHeight > el.clientHeight + 1));
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [ref, axis]);
  return overflowing;
}

/** `"x"`: horizontal only (tables). `"both"`: also a max-height scroller (code blocks). */
export type OverflowAxis = "x" | "both";

export interface OverflowRegionProps extends Omit<HTMLAttributes<HTMLDivElement>, "role" | "tabIndex" | "aria-label"> {
  /** Scroll axes to watch. Default `"x"` (`overflow-x-auto`); `"both"` uses `overflow-auto`. */
  axis?: OverflowAxis;
  /**
   * Name of the scrolled content (e.g. "Invoices table"). Announced as
   * `"<label> (scrollable)"` when the region is focused.
   */
  label: string;
  children: ReactNode;
}

/** Classes that make the scroller visible when focused (light + dark pair). */
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400";

export function OverflowRegion({ label, axis = "x", className, children, ...rest }: OverflowRegionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const overflowing = useHorizontalOverflow(ref, axis);
  return (
    <div
      {...rest}
      ref={ref}
      {...(overflowing ? { role: "region", tabIndex: 0, "aria-label": `${label} (scrollable)` } : {})}
      className={clsx("relative", axis === "both" ? "overflow-auto" : "overflow-x-auto", FOCUS_RING, className)}
    >
      {children}
    </div>
  );
}

OverflowRegion.displayName = "OverflowRegion";
